// Function that checks if the target numbers are included in the array of numbers chronologically
// Returns the amount of target numbers that are included in the array of numbers
import { error } from './logger';

export function progressCheck(targetArr, numberHistory) {

    let numPointer = 0;

    for (let i = 0; i < numberHistory.length; i++) {
        if (numberHistory[i] === targetArr[numPointer]) {
            numPointer++;
        }
    }

    return numPointer;
}

export const convertMillisToSeconds = (milliseconds) => {
    // Input validation
    if (typeof milliseconds !== 'number' || isNaN(milliseconds)) {
        error('Invalid input: milliseconds must be a number');
        return '0.00';
    }

    // Use absolute value and truncate to tenths of a second
    const absMs = Math.abs(milliseconds);
    const seconds = absMs / 1000;
    return (Math.floor(seconds * 10) / 10).toFixed(1);
}

// Share/Copy text to clipboard with fallback support
export const shareOrCopyText = async (text) => {
    if (navigator.share) {
        await navigator.share({ text });
        return 'shared';
    } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return 'copied';
    } else {
        // Fallback for older browsers
        const tmp = document.createElement('textarea');
        tmp.value = text;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
        return 'copied';
    }
}

// Game scoring constants (logistic time-based scoring)
const SCORING = {
    BASE_SCORE: 100,
    // Logistic time scoring parameters (seconds)
    TIME_MID_SECONDS: 150, // median / typical completion time
    TIME_CURVATURE: 1.2,  // controls steepness (1.2-1.6 recommended)
    // Step penalty parameter
    STEP_BETA: 0.1,
    // Weights for combining time vs steps
    W_TIME: 0.8,
    W_STEPS: 0.2,
    MIN_SCORE: 0,
    MAX_SCORE: 100,
};

// Boards give a fixed move budget and no operator can be skipped or reused out of
// order, so difficulty mainly comes down to how many moves that budget demands and
// how many of the operators require exact multiply/divide reasoning (versus simple
// addition/subtraction) to land on. This is a heuristic label, not a precise measure.
export const getBoardDifficulty = ({ operators = [], solution_length: solutionLength = 0 } = {}) => {
    const precisionOperatorCount = operators.filter((operator) => operator.startsWith('*') || operator.startsWith('/')).length;
    const difficultyScore = solutionLength + (precisionOperatorCount * 2);

    if (difficultyScore <= 10) return 'Easy';
    if (difficultyScore <= 12) return 'Medium';
    return 'Hard';
};

export const getBoardNavigation = (boardNum, latestBoardNum, playedBoardNumbers = []) => {
    const latest = Number(latestBoardNum);
    if (!Number.isInteger(Number(boardNum)) || Number(boardNum) < 1 || !Number.isFinite(latest) || latest < 1) {
        return { previous: null, next: null };
    }

    const playedBoards = new Set(playedBoardNumbers.map(Number));
    const findUnplayedBoard = (step) => {
        for (let candidate = Number(boardNum) + step; candidate >= 1 && candidate <= latest; candidate += step) {
            if (!playedBoards.has(candidate)) return candidate;
        }
        return null;
    };

    return {
        previous: findUnplayedBoard(-1),
        next: findUnplayedBoard(1),
    };
};

export const calculateGameScore = (timeTakenMs, extraStepsTaken) => {
    const {
        BASE_SCORE,
        TIME_MID_SECONDS,
        TIME_CURVATURE,
        STEP_BETA,
        W_TIME,
        W_STEPS,
        MIN_SCORE,
        MAX_SCORE
    } = SCORING;

    const t = Math.max(0, timeTakenMs) / 1000; // seconds

    // Logistic-like time score: closer to 0 -> closer to 1; centered around TIME_MID_SECONDS
    const timeScore = 1 / (1 + Math.pow(t / TIME_MID_SECONDS, TIME_CURVATURE));

    // Step score: simple diminishing return with linear factor
    const stepCount = Math.max(0, extraStepsTaken);
    const stepScore = 1 / (1 + STEP_BETA * stepCount);

    // Weighted combination scaled to BASE_SCORE
    let score = BASE_SCORE * (W_TIME * timeScore + W_STEPS * stepScore);

    // Clamp and return rounded score
    score = Math.max(MIN_SCORE, Math.min(MAX_SCORE, score));
    return Math.round(score);
}

export const getNumHistoryFromSolution = (targetNums, operation, solution) => {
    const numHistory = [targetNums[0]];
    for (let i = 1; i < solution.length + 1; i++) {
        const newNumber = getNewNumber(numHistory[i - 1], operation[solution[i - 1]]);
        numHistory.push(newNumber);
    }
    return numHistory;
}

export const getNewNumber = (currentNumber, operator) => {
    const operatorNumber = (operator.startsWith('+') || operator.startsWith('-'))
        ? parseInt(operator, 10)
        : parseInt(operator.slice(1), 10);
    let newNumber;

    // Perform the operation based on the operator
    // i.e. if operator is '+1', then add 1 to the current number
    if (operator.startsWith('+') || operator.startsWith('-')) {
        newNumber = currentNumber + operatorNumber;
    } else if (operator.startsWith('*')) {
        newNumber = currentNumber * operatorNumber;
    } else if (operator.startsWith('/')) {
        if (operatorNumber === 0) {
            error('Cannot divide by zero');
            return null;
        }

        if (!Number.isInteger(currentNumber / operatorNumber)) {
            error('Division must result in a whole number');
            return null;
        }

        newNumber = currentNumber / operatorNumber;
    }
    return newNumber;
}

// Replace an operator's leading symbol with its display glyph (* -> ×, / -> ÷)
export const formatOperatorSymbol = (operator) =>
    operator.replace(/^([+*/-])/, (symbol) => (symbol === '*' ? '×' : symbol === '/' ? '÷' : symbol));

// Replay a raw action log (operator strings, plus 'U' for undo and 'C' for clear) down
// to the sequence of operators still in effect at the end. Undos pop the most recent
// operator and clears wipe everything, mirroring how the board itself resets — so this
// reconstructs the true final path even across abandoned attempts.
export const replayActionHistory = (actionHistory) => {
    const stack = [];
    (actionHistory || []).forEach((action) => {
        if (action === 'C') {
            stack.length = 0;
        } else if (action === 'U') {
            stack.pop();
        } else {
            stack.push(action);
        }
    });
    return stack;
};

export const getOperationHistoryFromSolution = (operation, solution) => {
    const numHistory = [];
    for (let i = 0; i < solution.length; i++) {
        numHistory.push(operation[solution[i]]);
    }
    return numHistory;
}